/**
 * Tapcart-only lifecycle handler for refreshing stale data
 * Handles BFCache restore and app switching scenarios
 */
(function() {
  // Robust Tapcart detection
  function inApp() {
    try {
      if (window.__isTapcart === true) return true;
      if (window.webkit?.messageHandlers?.Tapcart) return true;           // iOS
      if (typeof window.CustomBlockJavascriptInterface !== 'undefined') return true; // Android
      if (window.Tapcart) return true;
    } catch {}
    return false;
  }

  // Constants
  var DIRTY_KEY = 'pack_dirty';
  var SEEN_KEY = 'pack_dirty_seen';
  var LAST_TS_KEY = '__last_refresh_ts';

  // Tracks initial visibility event after load to avoid first-load refresh
  var firstVisibleHandled = false;

  // Safe timestamp helpers
  function getTimestamp(key) {
    try {
      return parseInt(localStorage.getItem(key) || '0', 10);
    } catch (e) {
      return 0;
    }
  }

  function setTimestamp(key, value) {
    try {
      localStorage.setItem(key, String(value));
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }

  // Review page: primary target for lifecycle refresh.
  function isReview() {
    var path = window.personalised.config.routes.review;
    return new RegExp('^' + path + '(?:/|$)').test(window.location.pathname);
  }

  // Account page: also refreshed by lifecycle.
  // Coordination note: personalised-account-bridge.js runs on a separate custom page
  // (/pages/personalised-account-bridge) which uses Liquid {% layout none %}, so the lifecycle
  // script is NOT loaded there. The bridge redirects to /account only after confirming a valid
  // Shopify session. By the time the user lands on /account, the bridge has already finished
  // its work and exited. Lifecycle and bridge therefore never run concurrently — no coordination
  // is required. A lifecycle-triggered reload of /account is safe: LAST_TS_KEY and SEEN_KEY are
  // written only when the reload actually fires, so shouldRefresh() returns false on the next
  // resume event until pack_dirty is written again. There is no reload-loop risk.
  function isAccount() {
    var path = window.personalised.config.routes.account;
    return new RegExp('^' + path + '(?:/|$)').test(window.location.pathname);
  }

  // Library page: intentionally excluded from lifecycle refresh.
  // The library page is a static product catalog and does not display the user's current pack
  // state. Add-to-pack buttons read live localStorage at click time, so there is no stale-state
  // risk from a cached or resumed page. Reloading on resume would also disrupt browsing
  // (scroll position lost, filter state reset). Defined here for documentation clarity and
  // future-proofing; the exclusion is enforced by refreshPage() which only reloads on
  // isReview() || isAccount().
  function isLibrary() {
    var path = window.personalised.config.routes.library;
    return !!(path && new RegExp('^' + path + '(?:/|$)').test(window.location.pathname));
  }

  // Navigation type detection with fallbacks: 'navigate' | 'reload' | 'back_forward' | 'unknown'
  function getNavType() {
    try {
      var entry = (performance && typeof performance.getEntriesByType === 'function')
        ? performance.getEntriesByType('navigation')[0]
        : null;
      if (entry && entry.type) return entry.type; // modern: 'navigate' | 'reload' | 'back_forward'
      if (performance && performance.navigation && typeof performance.navigation.type === 'number') {
        switch (performance.navigation.type) {
          case 0: return 'navigate';
          case 1: return 'reload';
          case 2: return 'back_forward';
          default: return 'unknown';
        }
      }
    } catch (e) {}
    return 'unknown';
  }

  // Check if refresh is needed and not throttled
  function shouldRefresh() {
    var now = Date.now();
    var lastRefresh = getTimestamp(LAST_TS_KEY);
    
    // Throttle check - prevent multiple refreshes within configured throttle
    if (now - lastRefresh < window.personalised.config.tapcart.refreshThrottle) {
      return false;
    }
    
    // Compare dirty timestamp with last seen timestamp
    var packDirty = getTimestamp(DIRTY_KEY);
    var lastSeen = getTimestamp(SEEN_KEY);
    
    return packDirty > 0 && packDirty > lastSeen;
  }

  // Perform the refresh — only reloads on pages where stale pack state is a risk (review, account).
  // LAST_TS_KEY (throttle) and SEEN_KEY (dirty acknowledgement) are written only when a reload
  // actually fires. Writing them unconditionally would (a) burn the throttle window on pages that
  // do not reload (e.g. library), and (b) falsely acknowledge dirty state, preventing the review
  // page from refreshing on the next resume after a library-page bypass.
  function refreshPage() {
    if (isReview() || isAccount()) {
      setTimestamp(LAST_TS_KEY, Date.now());
      setTimestamp(SEEN_KEY, getTimestamp(DIRTY_KEY) || 0);
      location.reload();
    }
    // No-op on library and all other pages: throttle and dirty state are intentionally left
    // unchanged so a subsequent navigation to review or account can still trigger a refresh.
  }

  // Initialize listeners
  function init() {
    // BFCache restore listener
    window.addEventListener('pageshow', function(event) {
      var navType = getNavType();
      if ((event.persisted === true || navType === 'back_forward') && shouldRefresh()) {
        refreshPage();
      }
    });

    // App visibility change listener (for app switching)
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState !== 'visible') return;
      
      // Tapcart bypass: refresh immediately on first visibility event if pack is already dirty,
      // rather than deferring to the second event (the firstVisibleHandled guard below skips
      // the first event entirely). window.__tapcartBackBypassed prevents re-entry on the same
      // page load; it resets automatically when location.reload() creates a new page context.
      // The early SEEN_KEY write (Date.now()) has been removed: refreshPage() writes SEEN_KEY
      // correctly inside its reload block. Writing it here would falsely acknowledge dirty state
      // on pages that do not reload (e.g. library), blocking the review page on next resume.
      if (inApp() && shouldRefresh() && !window.__tapcartBackBypassed) {
        window.__tapcartBackBypassed = true;
        return refreshPage();
      }
      
      if (!firstVisibleHandled) { firstVisibleHandled = true; return; }
      if (shouldRefresh()) { refreshPage(); }
    });

    // Focus fallback for additional back-navigation detection
    window.addEventListener('focus', function() {
      // Fallback mirror of the visibilitychange bypass for Tapcart environments where
      // visibilitychange is unreliable. window.__tapcartBackBypassed coordinates both
      // handlers — whichever fires first sets the flag, preventing a duplicate refresh call.
      // Early SEEN_KEY write removed for the same reason as the visibilitychange handler above.
      if (inApp() && shouldRefresh() && !window.__tapcartBackBypassed) {
        window.__tapcartBackBypassed = true;
        return refreshPage();
      }
      
      // Only after initial visible has been seen (so we don't fire on first load),
      // and still guarded by shouldRefresh() which enforces throttle and dirty check.
      if (firstVisibleHandled && shouldRefresh()) {
        refreshPage();
      }
    });
  }

  // Delayed initialization - wait for Tapcart detection
  function initWhenReady() {
    var attempts = 0;
    var maxAttempts = 80; // ~2 seconds with 25ms intervals
    
    function checkAndInit() {
      if (inApp()) {
        init();
        return;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkAndInit, 25);
      }
    }
    
    checkAndInit();
  }

  // Start initialization
  initWhenReady();

})();
